import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper, 
  Link as MuiLink, 
  Alert, 
  Snackbar,
  IconButton, 
  InputAdornment,
  FormControl,
  FormHelperText,
  Tooltip,
  Collapse
} from '@mui/material';
import { Visibility, VisibilityOff, Info } from '@mui/icons-material';

// Password strength checker
const checkPasswordStrength = (password: string) => {
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  let strength = 0;
  let feedback = [];
  
  if (hasMinLength) strength++;
  if (hasUpperCase) strength++;
  if (hasLowerCase) strength++;
  if (hasNumbers) strength++;
  if (hasSpecialChar) strength++;
  
  if (!hasMinLength) feedback.push('At least 8 characters');
  if (!hasUpperCase) feedback.push('At least one uppercase letter');
  if (!hasLowerCase) feedback.push('At least one lowercase letter');
  if (!hasNumbers) feedback.push('At least one number');
  if (!hasSpecialChar) feedback.push('At least one special character');
  
  return { strength, feedback };
};

// Email validation
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const RegisterPage: React.FC = () => {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [farmName, setFarmName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Show Snackbar when error or success changes
  useEffect(() => {
    if (error || success) setShowSnackbar(true);
  }, [error, success]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{strength: number, feedback: string[]}>({strength: 0, feedback: []});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPasswordTips, setShowPasswordTips] = useState(false);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!name.trim()) errors.name = 'Full name is required';
    if (!farmName.trim()) errors.farmName = 'Farm name is required';
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (passwordStrength.strength < 3) {
      errors.password = 'Password is too weak';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle password change and check strength
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
    
    // Clear error when user starts typing
    if (fieldErrors.password) {
      setFieldErrors(prev => ({
        ...prev,
        password: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      const { error } = await signUp(email, password, name, farmName);
      
      if (error) {
        if (error.message.includes('already registered')) {
          setError('An account with this email already exists. Please log in instead.');
        } else {
          throw error;
        }
        return;
      }
      
      // Show success message and redirect to login after a short delay
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'Failed to create an account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrengthColor = () => {
    if (password.length === 0) return 'grey';
    if (passwordStrength.strength <= 2) return 'red';
    if (passwordStrength.strength <= 3) return 'orange';
    return 'green';
  };

  return (
    <Container component="main" maxWidth="xs">
      <Snackbar
        open={showSnackbar && (!!error || success)}
        autoHideDuration={4000}
        onClose={() => { setShowSnackbar(false); setError(''); setSuccess(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => { setShowSnackbar(false); setError(''); setSuccess(false); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success ? 'Registration successful! You can now log in.' : error}
        </Alert>
      </Snackbar>
      <Paper elevation={3} sx={{ 
        mt: { xs: 4, sm: 8 }, 
        p: { xs: 2, sm: 4 }, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        borderRadius: 2,
        boxShadow: '0 8px 16px rgba(0,0,0,0.05)'
      }}>
        <Typography component="h1" variant="h5" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
          Create Your Account
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          Get started with your strawberry growth tracking
        </Typography>
        
        {error && (
          <Alert 
            severity={error.includes('successful') ? 'success' : 'error'} 
            sx={{ width: '100%', mb: 3, borderRadius: 1 }}
          >
            {error}
          </Alert>
        )}
        
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            width: '100%',
            '& .MuiTextField-root': { mb: 2 }
          }}
          noValidate
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth error={!!fieldErrors.name}>
              <TextField
                required
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (fieldErrors.name) {
                    setFieldErrors(prev => ({ ...prev, name: '' }));
                  }
                }}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
            </FormControl>
            
            <FormControl fullWidth error={!!fieldErrors.farmName}>
              <TextField
                required
                id="farmName"
                label="Farm Name"
                name="farmName"
                autoComplete="organization"
                value={farmName}
                onChange={(e) => {
                  setFarmName(e.target.value);
                  if (fieldErrors.farmName) {
                    setFieldErrors(prev => ({ ...prev, farmName: '' }));
                  }
                }}
                error={!!fieldErrors.farmName}
                helperText={fieldErrors.farmName}
              />
            </FormControl>
          </Box>
          
          <FormControl fullWidth error={!!fieldErrors.email}>
            <TextField
              required
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) {
                  setFieldErrors(prev => ({ ...prev, email: '' }));
                }
              }}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || 'We\'ll never share your email with anyone else.'}
              type="email"
            />
          </FormControl>
          
          <FormControl fullWidth error={!!fieldErrors.password}>
            <TextField
              required
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={handlePasswordChange}
              error={!!fieldErrors.password}
              helperText={
                fieldErrors.password || 
                (password ? `${passwordStrength.strength}/5 strength` : '')
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <Tooltip title="Password requirements">
                      <IconButton 
                        onClick={() => setShowPasswordTips(!showPasswordTips)}
                        size="small"
                        sx={{ ml: 0.5 }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Collapse in={showPasswordTips}>
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" component="div" color="text.secondary" gutterBottom>
                  <strong>Password must contain:</strong>
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, '& li': { fontSize: '0.75rem' } }}>
                  {passwordStrength.feedback.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </Box>
              </Paper>
            </Collapse>
          </FormControl>
          
          <FormControl fullWidth error={!!fieldErrors.confirmPassword}>
            <TextField
              required
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirmPassword) {
                  setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                }
              }}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              mt: 2, 
              mb: 3, 
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
          
          <Box sx={{ textAlign: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <MuiLink 
                component={Link} 
                to="/login" 
                variant="body2"
                sx={{ fontWeight: 500 }}
              >
                Sign In
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterPage;
